import type { CellViewWire } from '@badger-sentinel/protocol';
import { useMemo } from 'react';

import { ModifierLadder, BlockBar } from './ModifierLadder.js';
import { PickerPanel } from './PickerPanel.js';
import {
  applyPick,
  pickRowsFor,
  pickerEmptyReason,
  targetForSubject,
  targetWords,
  type PickRow,
  type SlotId,
} from '../model/builder.js';
import { successLadder } from '../model/ladder.js';
import { missionLabel, paramSpecOf } from '../model/missions.js';
import { queuedMissionsByAsset } from '../model/queue.js';
import { useConsoleStore } from '../state/store.js';

/**
 * The order builder strip (design 1458–1605).
 *
 * Three slots, a picker, an estimate line, a full ladder on request, and one
 * commit — `ADD TO QUEUE`. It lives at the foot of the right rail, under the
 * tray, exactly where the design puts it.
 *
 * ## Adding to the queue is not irreversible, so it does not ask
 *
 * The design's strip has a confirm on the commit. Ours does not: a queued order
 * can be edited, reordered and cancelled for the rest of the window, and the
 * turn's only irreversible act is the clock reaching zero. The confirm this
 * phase built (`chrome/ConfirmInline.tsx`) is spent where something is actually
 * lost — cancelling an order, and applying a batch over a draft.
 *
 * ## Slots are drop targets and the affordance says so
 *
 * A slot is a button that opens a picker and a drop zone that takes a roster
 * row. No caption explains either; the design's own legend for drag targets is
 * one global line and this strip does not need it, because a slot reading `—`
 * beside a label reading `ASSET` is already the instruction.
 */

export interface OrderBuilderProps {
  readonly view: CellViewWire | null;
  /**
   * Whether this seat may commit — **the commit alone**, not the strip.
   *
   * False for a read-only seat and outside an open window (Q20), and it gates
   * `canQueue` and nothing else: the three slots, the picker and the estimate all
   * render either way. That is §3.3 invariant 3 working rather than an oversight —
   * a control that is simply absent teaches nothing, and a seat that cannot task
   * should be able to see what it is not permitted to do. `ReadOnlyBanner` is
   * where the console says why.
   */
  readonly enabled: boolean;
  /** The seat may never order, as opposed to not being able to right now. */
  readonly readOnly: boolean;
}

export function OrderBuilder(props: OrderBuilderProps): JSX.Element | null {
  const builder = useConsoleStore((state) => state.builder);
  const open = useConsoleStore((state) => state.builderOpen);
  const setOpen = useConsoleStore((state) => state.setBuilderOpen);
  const armedSlot = useConsoleStore((state) => state.armedSlot);
  const armSlot = useConsoleStore((state) => state.armSlot);
  const pickQuery = useConsoleStore((state) => state.pickQuery);
  const setPickQuery = useConsoleStore((state) => state.setPickQuery);
  const ladderOpen = useConsoleStore((state) => state.ladderOpen);
  const setLadderOpen = useConsoleStore((state) => state.setLadderOpen);
  const fillSlot = useConsoleStore((state) => state.fillSlot);
  const fillMission = useConsoleStore((state) => state.fillMission);
  const fillTarget = useConsoleStore((state) => state.fillTarget);
  const fillParam = useConsoleStore((state) => state.fillParam);
  const clearSlot = useConsoleStore((state) => state.clearSlot);
  const queueDraft = useConsoleStore((state) => state.queueDraft);
  const queue = useConsoleStore((state) => state.queue);
  const showToast = useConsoleStore((state) => state.showToast);

  const view = props.view;

  const ladder = useMemo(
    () =>
      view === null
        ? null
        : successLadder({
            view,
            draft: builder,
            queuedMissionsByAsset: queuedMissionsByAsset(queue),
          }),
    [view, builder, queue],
  );

  if (view === null) return null;

  const spec = builder.mission === null ? null : paramSpecOf(builder.mission);
  const paramValue = spec === null ? undefined : builder.params[spec.key];
  /*
   * A read-only seat is told at the top of the flow, not at the bottom of it.
   *
   * §3.3's third invariant keeps the commit on screen and disabled so the seat can
   * see what it is not permitted to do, and Phase 10 went further: `pickerEmptyReason`
   * exists so CREWCOM opening a picker reads *No assets assigned to this cell* rather
   * than the design's misleading *No valid options for this combination*. Both are
   * teaching, and both are right.
   *
   * What was missing is the statement at the top. A cadet could fill all three slots,
   * watch the estimate compute and find `ADD TO QUEUE` inert, with the only account of
   * it a 20 px chip at the far left of the tab strip. So the seat says so here, in the
   * row this strip already has for saying why an order cannot go — the pickers stay
   * open and keep teaching, and the refusal is no longer a surprise at the end.
   */
  const readOnlyBlock = props.readOnly
    ? { reason: 'SEAT IS READ-ONLY', detail: 'CREWCOM DOES NOT ISSUE ORDERS · RB §5.1' }
    : null;
  const blocked = readOnlyBlock ?? ladder?.block ?? null;
  const canQueue =
    props.enabled && builder.asset !== null && builder.mission !== null && blocked === null;

  const choose = (row: PickRow): void => {
    applyPick(row, {
      asset: (id) => fillSlot('asset', id),
      mission: fillMission,
      target: fillTarget,
      param: (value) => {
        if (spec !== null) fillParam(spec.key, value);
      },
    });
  };

  const assetWords =
    builder.asset === null
      ? { id: '—', sub: '' }
      : {
          id: builder.asset,
          sub: view.friendly.find((candidate) => candidate.id === builder.asset)?.typeName ?? '',
        };
  const target = targetWords(view, builder.target);

  return (
    <div
      data-testid="order-builder"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-2)',
        borderTop: '1px solid var(--border-strong)',
      }}
    >
      <button
        type="button"
        data-testid="builder-toggle"
        aria-expanded={open}
        title="ORDER BUILDER (D)"
        onClick={() => setOpen(!open)}
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 34,
          padding: '0 9px 0 10px',
          background: 'var(--surface-3)',
          border: 0,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flex: 'none',
            fontFamily: 'var(--font-data)',
            fontSize: 12,
            color: 'var(--text-3)',
            transform: open ? 'rotate(90deg)' : 'none',
          }}
        >
          ›
        </span>
        <span style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Pip filled={builder.asset !== null} />
          <Pip filled={builder.mission !== null} />
          <Pip filled={builder.target !== null} />
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            overflow: 'hidden',
          }}
        >
          <Crumb text={assetWords.id} filled={builder.asset !== null} colour="var(--blue)" />
          <Sep />
          <Crumb
            text={builder.mission === null ? '—' : missionLabel(builder.mission)}
            filled={builder.mission !== null}
            colour="var(--primary)"
          />
          <Sep />
          <Crumb text={target.id} filled={builder.target !== null} colour="var(--red)" />
        </span>
        <span
          data-testid="builder-estimate"
          style={{
            flex: 'none',
            fontFamily: 'var(--font-data)',
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: blocked === null ? 'var(--text)' : 'var(--red-text)',
          }}
        >
          {blocked !== null ? 'BLOCKED' : (ladder?.headline ?? '')}
        </span>
      </button>

      {open && (
        <div style={{ flex: 'none', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <SlotRow
              slot="asset"
              label="ASSET"
              id={assetWords.id}
              sub={assetWords.sub}
              filled={builder.asset !== null}
              armed={armedSlot === 'asset'}
              colour="var(--blue)"
              onOpen={() => armSlot(armedSlot === 'asset' ? null : 'asset')}
              onClear={() => clearSlot('asset')}
              onDropId={(id) => {
                if (view.friendly.some((candidate) => candidate.id === id)) fillSlot('asset', id);
                else showToast(id + ' IS NOT ONE OF YOUR ASSETS', 'warn');
              }}
            />

            <SlotRow
              slot="mission"
              label="MISSION"
              id={builder.mission === null ? '—' : missionLabel(builder.mission)}
              sub={
                builder.mission === null
                  ? ''
                  : spec === null
                    ? ''
                    : spec.label +
                      ' · ' +
                      (paramValue === undefined ? 'NOT SET' : String(paramValue))
              }
              filled={builder.mission !== null}
              armed={armedSlot === 'mission'}
              colour="var(--primary)"
              onOpen={() => armSlot(armedSlot === 'mission' ? null : 'mission')}
              onClear={() => clearSlot('mission')}
            />

            {/* The fourth thing an order can say, inside the slot that decides
                whether it exists at all. `model/missions.ts` says why it is not
                a fourth slot. */}
            {spec !== null && (
              <button
                type="button"
                data-testid="param-row"
                data-param-key={spec.key}
                onClick={() => armSlot(armedSlot === 'param' ? null : 'param')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  height: 30,
                  padding: '0 12px 0 26px',
                  background: 'transparent',
                  border: 0,
                  borderBottom: '1px solid var(--rule-faint)',
                  borderLeft:
                    '2px solid ' + (paramValue === undefined ? 'var(--warn)' : 'var(--primary)'),
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    width: 54,
                    fontFamily: 'var(--font-ui)',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '.14em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                  }}
                >
                  {spec.label}
                </span>
                <span
                  data-testid="param-value"
                  style={{
                    flex: 1,
                    fontFamily: 'var(--font-data)',
                    fontSize: 12,
                    fontWeight: 600,
                    color: paramValue === undefined ? 'var(--warn)' : 'var(--text)',
                  }}
                >
                  {paramValue === undefined
                    ? spec.required
                      ? 'REQUIRED'
                      : 'DEFAULT'
                    : String(paramValue)}
                </span>
              </button>
            )}

            <SlotRow
              slot="target"
              label="TARGET"
              id={target.id}
              sub={target.sub}
              filled={builder.target !== null}
              armed={armedSlot === 'target'}
              colour="var(--red)"
              onOpen={() => armSlot(armedSlot === 'target' ? null : 'target')}
              onClear={() => clearSlot('target')}
              onDropId={(id) => {
                const dropped = targetForSubject(view, id);
                if (dropped === null) showToast(id + ' CANNOT BE A TARGET', 'warn');
                else fillTarget(dropped);
              }}
            />

            {armedSlot !== null && (
              <PickerPanel
                title={pickerTitle(armedSlot, spec?.label ?? '')}
                rows={pickRowsFor(view, builder, armedSlot)}
                empty={pickerEmptyReason(view)}
                query={pickQuery}
                onQuery={setPickQuery}
                onChoose={choose}
                onClose={() => armSlot(null)}
              />
            )}
          </div>

          <div
            style={{
              flex: 'none',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              background: 'var(--surface-2)',
              borderTop: '1px solid var(--border-strong)',
            }}
          >
            {ladderOpen && ladder !== null && (
              <div
                style={{
                  position: 'absolute',
                  left: 8,
                  right: 8,
                  bottom: '100%',
                  zIndex: 'var(--z-builder)',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 340,
                  marginBottom: 8,
                }}
              >
                <ModifierLadder
                  testId="builder-ladder"
                  title={
                    (builder.mission === null ? 'ESTIMATE' : missionLabel(builder.mission)) +
                    ' · ' +
                    ladder.headline
                  }
                  rows={ladder.rows}
                  footnote={ladder.footnote}
                  onClose={() => setLadderOpen(false)}
                />
              </div>
            )}

            {ladder !== null && (
              <div
                data-testid="estimate-row"
                style={{
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: 34,
                  padding: '0 8px 0 12px',
                  background: 'var(--surface)',
                  borderBottom: '1px solid var(--rule)',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: '.14em',
                    textTransform: 'uppercase',
                    color: 'var(--text-muted)',
                  }}
                >
                  ESTIMATE
                </span>
                <span
                  data-testid="estimate-value"
                  style={{
                    flex: 'none',
                    fontFamily: 'var(--font-data)',
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'var(--text)',
                  }}
                >
                  {ladder.headline}
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: 'var(--font-prose)',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: 'var(--text-muted)',
                  }}
                >
                  {ladder.top}
                </span>
                {ladder.escalation !== null && (
                  <span
                    data-testid="estimate-escalation"
                    style={{
                      flex: 'none',
                      fontFamily: 'var(--font-data)',
                      fontSize: 12,
                      letterSpacing: '.08em',
                      whiteSpace: 'nowrap',
                      color: 'var(--warn)',
                    }}
                  >
                    ESC {ladder.escalation}
                  </span>
                )}
                <button
                  type="button"
                  data-testid="estimate-why"
                  aria-expanded={ladderOpen}
                  onClick={() => setLadderOpen(!ladderOpen)}
                  style={{
                    flex: 'none',
                    height: 22,
                    padding: '0 8px',
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    color: 'var(--text-3)',
                    fontFamily: 'var(--font-data)',
                    fontSize: 11,
                    letterSpacing: '.08em',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                  }}
                >
                  WHY ›
                </button>
              </div>
            )}

            {blocked !== null && <BlockBar block={blocked} />}

            {/* The ROE bill on an order the server will **accept** (M3-D13). It
                does not block, it does not look like a rejection, and it carries
                the price — which is the whole of RB §7.3's training point. */}
            {ladder?.roe != null && !ladder.roe.blocks && (
              <div
                data-testid="roe-warning"
                style={{
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: 30,
                  padding: '5px 12px',
                  background: 'var(--surface)',
                  borderBottom: '1px solid var(--warn)',
                }}
              >
                <span
                  style={{
                    flex: 'none',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '.14em',
                    color: 'var(--warn)',
                  }}
                >
                  ROE
                </span>
                <span
                  data-testid="roe-note"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontFamily: 'var(--font-prose)',
                    fontSize: 12,
                    color: 'var(--text-2)',
                  }}
                >
                  {ladder.roe.note}
                </span>
                <span
                  data-testid="roe-price"
                  style={{
                    flex: 'none',
                    fontFamily: 'var(--font-data)',
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: 'var(--warn)',
                  }}
                >
                  {'−' +
                    String(ladder.roe.vp) +
                    ' VP · ESC ×' +
                    String(ladder.roe.escalationFactor)}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px' }}>
              <button
                type="button"
                data-testid="add-order"
                disabled={!canQueue}
                onClick={() => {
                  if (queueDraft()) showToast('ORDER QUEUED');
                }}
                style={{
                  flex: 1,
                  height: 40,
                  background: canQueue ? 'var(--primary)' : 'transparent',
                  border: '1px solid ' + (canQueue ? 'var(--primary)' : 'var(--border)'),
                  borderRadius: 'var(--radius)',
                  color: canQueue ? 'var(--primary-on)' : 'var(--text-disabled)',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 15,
                  fontWeight: 700,
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  cursor: canQueue ? 'pointer' : 'default',
                }}
              >
                ADD TO QUEUE ⏎
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function pickerTitle(slot: SlotId, paramLabel: string): string {
  switch (slot) {
    case 'asset':
      return 'ASSET';
    case 'mission':
      return 'MISSION';
    case 'target':
      return 'TARGET';
    case 'param':
      return paramLabel;
  }
}

function SlotRow(props: {
  readonly slot: SlotId;
  readonly label: string;
  readonly id: string;
  readonly sub: string;
  readonly filled: boolean;
  readonly armed: boolean;
  readonly colour: string;
  readonly onOpen: () => void;
  readonly onClear: () => void;
  readonly onDropId?: (id: string) => void;
}): JSX.Element {
  const takesDrop = props.onDropId !== undefined;
  return (
    <div
      data-testid={'slot-' + props.slot}
      data-slot-filled={props.filled ? 'true' : 'false'}
      onDragOver={takesDrop ? (event) => event.preventDefault() : undefined}
      onDrop={
        takesDrop
          ? (event) => {
              event.preventDefault();
              const id = event.dataTransfer.getData('text/plain');
              if (id !== '') props.onDropId?.(id);
            }
          : undefined
      }
      style={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid var(--rule-faint)',
        borderLeft:
          '2px solid ' +
          (props.armed ? 'var(--primary)' : props.filled ? props.colour : 'var(--border)'),
      }}
    >
      <button
        type="button"
        data-testid={'slot-open-' + props.slot}
        aria-expanded={props.armed}
        onClick={props.onOpen}
        style={{
          display: 'flex',
          flex: 1,
          minWidth: 0,
          alignItems: 'center',
          gap: 10,
          height: 36,
          padding: '0 0 0 12px',
          background: 'transparent',
          border: 0,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            flex: 'none',
            width: 54,
            fontFamily: 'var(--font-ui)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          {props.label}
        </span>
        <span
          data-testid={'slot-value-' + props.slot}
          style={{
            flex: 'none',
            fontFamily: 'var(--font-data)',
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '.04em',
            color: props.filled ? props.colour : 'var(--text-dim)',
          }}
        >
          {props.id}
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: 'var(--font-prose)',
            fontSize: 13,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--text-3)',
          }}
        >
          {props.sub}
        </span>
      </button>
      {props.filled && (
        <button
          type="button"
          data-testid={'slot-clear-' + props.slot}
          aria-label={'CLEAR ' + props.label}
          onClick={props.onClear}
          style={{
            flex: 'none',
            width: 28,
            background: 'transparent',
            border: 0,
            borderLeft: '1px solid var(--rule-faint)',
            color: 'var(--text-3)',
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

function Pip(props: { readonly filled: boolean }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 4,
        height: 13,
        background: props.filled ? 'var(--primary)' : 'var(--border-strong)',
      }}
    />
  );
}

function Sep(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        flex: 'none',
        fontFamily: 'var(--font-data)',
        fontSize: 11,
        color: 'var(--text-dim)',
      }}
    >
      ›
    </span>
  );
}

function Crumb(props: {
  readonly text: string;
  readonly filled: boolean;
  readonly colour: string;
}): JSX.Element {
  return (
    <span
      style={{
        flex: 'none',
        minWidth: 0,
        fontFamily: 'var(--font-data)',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '.02em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: props.filled ? props.colour : 'var(--text-dim)',
      }}
    >
      {props.text}
    </span>
  );
}
